import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

/** Quick-pick palette shown at the top of the picker. */
const PRESETS = [
  '#2563EB', '#0D9488', '#D97706', '#DC2626', '#7C3AED', '#0891B2',
  '#65A30D', '#DB2777', '#475569', '#B45309', '#0F766E', '#4F46E5',
  '#EF4444', '#F59E0B', '#10B981', '#3B82F6', '#8B5CF6', '#94A3B8',
];

const HEX_PATTERN = /^#([0-9a-f]{6})$/i;

function hexToRgb(hex: string): string {
  const match = HEX_PATTERN.exec(hex);
  if (!match) return '';
  const int = parseInt(match[1], 16);
  return `${(int >> 16) & 255}, ${(int >> 8) & 255}, ${int & 255}`;
}

/**
 * A clickable colour swatch that opens a compact popover picker anchored to
 * itself — deliberately not a centred modal, so the class list stays visible.
 *
 * Selection previews live via `onChange`; Cancel restores the colour the swatch
 * had when the popover opened.
 */
export default function ColorSwatchPicker({
  color,
  onChange,
  label,
  size = 'md',
}: {
  color: string;
  onChange: (color: string) => void;
  /** Used for the tooltip and accessible name, e.g. the class value. */
  label: string;
  size?: 'sm' | 'md';
}) {
  const [open, setOpen] = useState(false);
  const [hexDraft, setHexDraft] = useState(color);
  const openingColor = useRef(color);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const popoverRef = useRef<HTMLDivElement | null>(null);
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);

  useEffect(() => setHexDraft(color), [color]);

  // Anchor the popover, flipping/shifting to stay inside the viewport.
  useLayoutEffect(() => {
    if (!open) {
      setPosition(null);
      return;
    }
    const anchor = buttonRef.current;
    const popover = popoverRef.current;
    if (!anchor || !popover) return;

    const anchorRect = anchor.getBoundingClientRect();
    const popRect = popover.getBoundingClientRect();
    const margin = 8;

    let top = anchorRect.bottom + 6;
    if (top + popRect.height > window.innerHeight - margin) {
      top = Math.max(margin, anchorRect.top - popRect.height - 6);
    }
    let left = anchorRect.left;
    left = Math.min(Math.max(margin, left), window.innerWidth - popRect.width - margin);

    setPosition({ top, left });
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (popoverRef.current?.contains(target) || buttonRef.current?.contains(target)) return;
      setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onChange(openingColor.current); // Escape behaves as Cancel
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown, true);
    };
  }, [open, onChange]);

  const commitHex = (value: string) => {
    const candidate = value.startsWith('#') ? value : `#${value}`;
    setHexDraft(candidate);
    if (HEX_PATTERN.test(candidate)) onChange(candidate.toLowerCase());
  };

  const dimensions = size === 'sm' ? 'h-5 w-7' : 'h-6 w-9';

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        title={`Change colour — ${label}`}
        aria-label={`Change colour for ${label}`}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => {
          openingColor.current = color;
          setOpen((current) => !current);
        }}
        className={`${dimensions} flex-shrink-0 cursor-pointer rounded border border-slate-300 transition hover:scale-105 hover:border-slate-500 hover:shadow focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 ${
          open ? 'ring-2 ring-blue-500' : ''
        }`}
        style={{ backgroundColor: color }}
      />

      {/*
        Portalled to <body> — this popover lives inside panels (e.g. the
        Symbology panel) that are `transform`ed for their own slide-in
        animation, and a `transform` on an ancestor creates a NEW containing
        block for `position: fixed` descendants. Without the portal, "fixed"
        would resolve against that panel instead of the viewport, and the
        panel's own `overflow-hidden` would clip this popover — it would
        open mispositioned or invisible instead of anchored to the swatch.
      */}
      {open && createPortal(
        <div
          ref={popoverRef}
          role="dialog"
          aria-label={`Colour picker for ${label}`}
          className="fixed z-[7000] w-56 rounded-xl border border-slate-200 bg-white p-3 shadow-[0_12px_32px_-8px_rgba(15,41,66,0.35)]"
          style={{
            top: position?.top ?? -9999,
            left: position?.left ?? -9999,
            visibility: position ? 'visible' : 'hidden',
          }}
        >
          <p className="mb-2 truncate text-[11px] font-semibold text-slate-700" title={label}>
            {label}
          </p>

          <div className="mb-2 grid grid-cols-6 gap-1">
            {PRESETS.map((preset) => (
              <button
                key={preset}
                type="button"
                title={preset}
                aria-label={preset}
                onClick={() => onChange(preset)}
                className={`h-6 w-full rounded border transition hover:scale-110 ${
                  preset.toLowerCase() === color.toLowerCase()
                    ? 'border-slate-800 ring-1 ring-slate-800'
                    : 'border-slate-200'
                }`}
                style={{ backgroundColor: preset }}
              />
            ))}
          </div>

          <div className="mb-2 flex items-center gap-2">
            <input
              type="color"
              value={HEX_PATTERN.test(color) ? color : '#000000'}
              onChange={(event) => onChange(event.target.value)}
              aria-label="Custom colour"
              className="h-8 w-10 cursor-pointer rounded border border-slate-200 bg-white p-0.5"
            />
            <div className="min-w-0 flex-1">
              <input
                value={hexDraft}
                onChange={(event) => commitHex(event.target.value)}
                spellCheck={false}
                aria-label="Hex colour value"
                className="w-full rounded border border-slate-200 px-1.5 py-1 font-mono text-[11px] uppercase outline-none focus:border-blue-400"
              />
              <p className="mt-0.5 truncate font-mono text-[10px] text-slate-400">
                rgb({hexToRgb(color) || '—'})
              </p>
            </div>
          </div>

          <div className="flex justify-end gap-1.5">
            <button
              type="button"
              onClick={() => {
                onChange(openingColor.current);
                setOpen(false);
              }}
              className="rounded-lg border border-slate-200 px-2.5 py-1 text-[11px] font-medium text-slate-600 transition hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="rounded-lg bg-[#0F2942] px-3 py-1 text-[11px] font-semibold text-white transition hover:bg-[#16395C]"
            >
              Done
            </button>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}
